/**
 * Detect a successful GHL / LeadConnector calendar booking and fire a neutral,
 * action-based Meta Pixel custom conversion event.
 *
 * IMPORTANT — Meta policy compliance:
 *   Meta's Special Ad Category policy blocks event names, URL rules, and custom
 *   parameters that reference protected health conditions (menopause, PMOS, etc.).
 *   Everything sent to Meta from here is therefore strictly action-based and
 *   condition-agnostic. The "origin" of the lead is tracked internally in
 *   sessionStorage for our own attribution, but never sent to Meta.
 *
 * Detection (both supported, fires once per session):
 *   1. postMessage:    iframe posts a message we can recognize as a booking.
 *   2. URL fallback:   GHL is configured to redirect to /book?booked=1.
 */

const ORIGIN_KEY = 'glow_book_origin';
const FIRED_KEY = 'glow_pixel_book_fired';
const GHL_ORIGINS = [
  'https://api.leadconnectorhq.com',
  'https://app.leadconnectorhq.com',
  'https://msgsndr.com',
];

// Neutral, action-based event name (Meta-policy safe).
// Same event for every booking, regardless of which landing page brought them.
const PIXEL_EVENT_NAME = 'Schedule_Intro_Call';

// Neutral funnel codes, NEVER use the condition name as a parameter value.
// The mapping (e.g., F001 = Menopause page) lives in your notes / Meta ad set
// names, not in any parameter Meta sees. Add a code per landing page when you
// want a per-page Custom Conversion in Ads Manager.
const FUNNEL_CODE_BY_ORIGIN = {
  menopause: 'F001',
  // perimenopause: 'F002',
  // pmos: 'F003',
  // pmdd: 'F004',
  // postpartum: 'F005',
  // thyroid: 'F006',
};

const looksLikeBookingMessage = (data) => {
  if (!data) return false;
  const haystack = (typeof data === 'string')
    ? data.toLowerCase()
    : JSON.stringify(data).toLowerCase();
  return (
    haystack.includes('appointment') ||
    haystack.includes('booked') ||
    haystack.includes('booking-confirmed') ||
    haystack.includes('calendar.widget.booked') ||
    haystack.includes('form-submitted')
  );
};

const fireConversion = () => {
  if (typeof window === 'undefined') return;
  if (sessionStorage.getItem(FIRED_KEY)) return;

  const origin = sessionStorage.getItem(ORIGIN_KEY);
  const funnelCode = origin ? FUNNEL_CODE_BY_ORIGIN[origin] : null;

  // Fire the neutral pixel event. Only neutral parameters allowed (no health terms).
  // Including a funnel_id lets you create a per-page Custom Conversion in Meta:
  //   "Schedule_Intro_Call where funnel_id = F001"  (compliant — Meta never sees
  //   the term 'menopause').
  const params = funnelCode ? { funnel_id: funnelCode } : {};
  if (window.fbq) {
    window.fbq('trackCustom', PIXEL_EVENT_NAME, params);
  }
  if (window.gtag) {
    window.gtag('event', 'schedule_intro_call', params);
  }

  // Internal-only: push the original origin slug (e.g., 'menopause') to dataLayer
  // for our own analytics / GTM. Never goes to Meta.
  if (window.dataLayer && origin) {
    window.dataLayer.push({
      event: 'glow_internal_booking_attributed',
      funnel_origin: origin,
      funnel_id: funnelCode,
    });
  }

  sessionStorage.setItem(FIRED_KEY, '1');
  sessionStorage.removeItem(ORIGIN_KEY);
};

/**
 * Attach booking listeners. Returns a cleanup function.
 * Should be called once on /book mount.
 */
export const attachBookingPixelListener = () => {
  if (typeof window === 'undefined') return () => {};

  // 1. URL-param fallback: if GHL redirects to /book?booked=1 we fire immediately
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('booked') === '1') {
      fireConversion();
    }
  } catch { /* ignore malformed URL */ }

  // 2. postMessage from GHL iframe
  const handleMessage = (event) => {
    if (!event.origin) return;
    if (!GHL_ORIGINS.some((o) => event.origin.startsWith(o))) return;
    if (looksLikeBookingMessage(event.data)) {
      fireConversion();
    }
  };

  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};
